import { useSaveSettings } from '@ais-ui/websockets';
import { useSettings } from '../../Settings/SettingsProvider';
import { useCallback, useMemo } from 'react';
import {
  DEFAULT_S57_CONFIG,
  resolveS57LayerName,
  S57Config,
  S57LayerId,
} from './s57Presets';

/** Migrate old config shape ({ preset, customCategories }) to new ({ selectedLayers }) */
const migrateConfig = (raw: unknown): S57Config => {
  if (!raw || typeof raw !== 'object') return DEFAULT_S57_CONFIG;

  // New format — already has selectedLayers
  if (
    'selectedLayers' in raw &&
    Array.isArray((raw as S57Config).selectedLayers)
  ) {
    return raw as S57Config;
  }

  // Old format — { preset: 'all' | 'navigation' | ... }
  if ('preset' in raw) {
    const preset = (raw as { preset: string }).preset;
    if (preset === 'all' || !preset) return DEFAULT_S57_CONFIG;

    const validLayers: S57LayerId[] = [
      'navigation',
      'cables',
      'depths',
      'traffic',
    ];
    if (validLayers.includes(preset as S57LayerId)) {
      return { selectedLayers: [preset as S57LayerId] };
    }
  }

  return DEFAULT_S57_CONFIG;
};

export const useS57Config = () => {
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();

  const config: S57Config = useMemo(
    () => migrateConfig(settings['ribbonPlugin.settings'].s57_config),
    [settings],
  );

  const layerName = useMemo(() => resolveS57LayerName(config), [config]);

  const updateConfig = useCallback(
    (next: S57Config) => {
      const settingsToSave = {
        ...settings,
        'ribbonPlugin.settings': {
          ...settings['ribbonPlugin.settings'],
          s57_config: next,
        },
      };
      mutateSettings(settingsToSave);
    },
    [settings, mutateSettings],
  );

  return { config, layerName, updateConfig };
};
